import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  countryParamPresent,
  useLandingCountryDetection,
} from "../../roofingLight/landingCountryDetect";
import { leadCountryForTestDrive } from "../../roofingLight/testDrive/testDriveLead";
import DemoCallCalendar from "./DemoCallCalendar.jsx";
import { DEMO_CALL_COPY } from "./demoCallContent.js";
import { buildDemoCallLead } from "./demoCallLead.js";
import {
  AVAILABILITY_ZONE,
  formatConfirmation,
  formatDayLabel,
  formatPreferTimeSlot,
  formatSlotTime,
  isMonthReachable,
  isSelectableDate,
  shiftMonth,
  slotKey,
  slotsForDate,
  timeZoneCaption,
  timeZoneOptions,
  userTimeZone,
  validateDemoCall,
} from "./demoCallSchedule.js";
import { monthOfZoneToday } from "./demoCallTimeZone.js";

const INITIAL_FIELDS = {
  fullName: "",
  workEmail: "",
  mobile: "",
  website: "",
};

function backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

const DemoCallModal = ({ isOpen, onClose, now, timeZone }) => {
  const clock = now ?? new Date();
  const initialZone = timeZone || userTimeZone();
  const [searchParams] = useSearchParams();
  const detection = useLandingCountryDetection();
  const [zone, setZone] = useState(initialZone);
  const [visibleMonth, setVisibleMonth] = useState(() => monthOfZoneToday(clock, initialZone));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [status, setStatus] = useState("form");
  const [errorCode, setErrorCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [zoneOptions, setZoneOptions] = useState(() => timeZoneOptions(clock, initialZone));
  const clockRef = useRef(clock);
  const propZoneRef = useRef(timeZone);
  const submittingRef = useRef(false);
  const requestRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  clockRef.current = clock;
  propZoneRef.current = timeZone;
  isOpenRef.current = isOpen;

  useEffect(() => {
    if (!isOpen) {
      requestRef.current += 1;
      submittingRef.current = false;
      setSubmitting(false);
      setSelectedDate(null);
      setSelectedSlot(null);
      setFields(INITIAL_FIELDS);
      setStatus("form");
      setErrorCode("");
      return;
    }

    const openingZone = propZoneRef.current || userTimeZone();
    setZone(openingZone);
    setZoneOptions(timeZoneOptions(clockRef.current, openingZone));
    setVisibleMonth(monthOfZoneToday(clockRef.current, openingZone));
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const slots = selectedDate ? slotsForDate(selectedDate, clock, zone) : [];
  const errorMessage = errorCode ? DEMO_CALL_COPY.errors[errorCode] : "";

  const handleField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
  };

  const handleSelectDate = (day) => {
    setSelectedDate(day);
    setSelectedSlot(null);
    setErrorCode("");
  };

  const handleZoneChange = (event) => {
    const next = event.target.value;
    setZone(next);
    setSelectedSlot(null);
    setErrorCode("");
    if (selectedDate && !isSelectableDate(selectedDate, clock, next)) {
      setSelectedDate(null);
    }
    setVisibleMonth((month) => (
      isMonthReachable(month, clock, next) ? month : monthOfZoneToday(clock, next)
    ));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const code = validateDemoCall({ ...fields, slot: selectedSlot }, clock, zone);
    if (code) {
      setErrorCode(code);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    submittingRef.current = true;
    setSubmitting(true);
    setErrorCode("");
    const stillOpen = () => isOpenRef.current && requestRef.current === requestId;
    try {
      const detectedCountry = countryParamPresent(searchParams)
        ? null
        : await detection.whenSettled();
      const country = leadCountryForTestDrive(searchParams, detectedCountry);
      const lead = buildDemoCallLead({
        name: fields.fullName,
        phone: fields.mobile,
        email: fields.workEmail,
        website: fields.website,
        preferTimeSlot: formatPreferTimeSlot(selectedSlot, AVAILABILITY_ZONE),
        country,
        pageUrl: window.location.href,
        search: searchParams,
      });
      const url = backendUrl();
      if (!lead || !url) {
        if (stillOpen()) setErrorCode("submit");
        return;
      }
      const response = await fetch(`${url}/api/marketing/roofing-demo-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lead),
      });
      if (!stillOpen()) return;
      if (response.ok) setStatus("success");
      else setErrorCode("submit");
    } catch {
      if (stillOpen()) setErrorCode("submit");
    } finally {
      if (requestRef.current === requestId) submittingRef.current = false;
      if (stillOpen()) setSubmitting(false);
    }
  };

  return (
    <div className="quiet-modal-overlay" onClick={onClose} data-testid="demo-call-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-call-title"
        className="quiet-modal-card quiet-demo-modal-card"
        onClick={(event) => event.stopPropagation()}
        data-testid="demo-call-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors p-1"
          aria-label={DEMO_CALL_COPY.closeLabel}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {status === "success" ? (
          <div className="quiet-demo-success" data-testid="demo-call-success">
            <h2 id="demo-call-title" className="quiet-demo-title">
              {DEMO_CALL_COPY.successTitle}
            </h2>
            <p className="quiet-demo-subtitle" data-testid="demo-call-success-when">
              {formatConfirmation(selectedSlot, zone)}.
            </p>
            <p className="quiet-demo-tz">{timeZoneCaption(selectedSlot, zone)}</p>
            <button
              type="button"
              className="quiet-cta quiet-cta-primary"
              onClick={onClose}
              data-testid="demo-call-success-close"
            >
              {DEMO_CALL_COPY.successClose}
            </button>
          </div>
        ) : (
          <>
            <h2 id="demo-call-title" className="quiet-demo-title">
              {DEMO_CALL_COPY.title}
            </h2>
            <p className="quiet-demo-subtitle">{DEMO_CALL_COPY.subtitle}</p>
            <form onSubmit={handleSubmit} className="quiet-modal-form" data-testid="demo-call-form" noValidate>
              <div className="quiet-field quiet-demo-zone">
                <label htmlFor="demo-time-zone" className="quiet-field-label">
                  {DEMO_CALL_COPY.timeZoneLabel}
                </label>
                <div className="quiet-select-wrap">
                  <select
                    id="demo-time-zone"
                    className="quiet-select"
                    value={zone}
                    onChange={handleZoneChange}
                    data-testid="demo-call-timezone"
                  >
                    {zoneOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="quiet-demo-hours" data-testid="demo-call-hours">
                  {DEMO_CALL_COPY.salesHours}
                </p>
              </div>
              <div className="quiet-demo-layout">
                <DemoCallCalendar
                  visibleMonth={visibleMonth}
                  selectedDate={selectedDate}
                  now={clock}
                  timeZone={zone}
                  onSelectDate={handleSelectDate}
                  onShiftMonth={(delta) => setVisibleMonth((month) => shiftMonth(month, delta))}
                />
                <div>
                  {selectedDate ? (
                    <div
                      className="quiet-demo-slot-list"
                      role="radiogroup"
                      aria-label={DEMO_CALL_COPY.slotListLabel}
                    >
                      {slots.map((slot) => {
                        const selected = selectedSlot?.getTime() === slot.getTime();
                        return (
                          <button
                            key={slotKey(slot, zone)}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={selected ? "quiet-demo-slot quiet-demo-slot-selected" : "quiet-demo-slot"}
                            data-testid={`demo-slot-${slotKey(slot, zone)}`}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setErrorCode("");
                            }}
                          >
                            {formatSlotTime(slot, zone)}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="quiet-demo-tz">{DEMO_CALL_COPY.selectDateHint}</p>
                  )}
                </div>
              </div>

              {selectedDate && (
                <p className="quiet-demo-picked" data-testid="demo-call-picked">
                  {selectedSlot
                    ? formatConfirmation(selectedSlot, zone)
                    : `${formatDayLabel(selectedDate)}. ${DEMO_CALL_COPY.pickTimeHint}`}
                </p>
              )}

              <div className="quiet-demo-fields">
                <div className="quiet-field">
                  <label htmlFor="demo-full-name" className="quiet-field-label">
                    {DEMO_CALL_COPY.fullNameLabel}
                  </label>
                  <input
                    id="demo-full-name"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    value={fields.fullName}
                    onChange={handleField}
                    className="quiet-input"
                    placeholder={DEMO_CALL_COPY.fullNamePlaceholder}
                    data-testid="demo-call-name"
                  />
                </div>
                <div className="quiet-field">
                  <label htmlFor="demo-work-email" className="quiet-field-label">
                    {DEMO_CALL_COPY.emailLabel}
                  </label>
                  <input
                    id="demo-work-email"
                    name="workEmail"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={fields.workEmail}
                    onChange={handleField}
                    className="quiet-input"
                    placeholder={DEMO_CALL_COPY.emailPlaceholder}
                    data-testid="demo-call-email"
                  />
                </div>
                <div className="quiet-field">
                  <label htmlFor="demo-mobile" className="quiet-field-label">
                    {DEMO_CALL_COPY.mobileLabel}
                  </label>
                  <input
                    id="demo-mobile"
                    name="mobile"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={fields.mobile}
                    onChange={handleField}
                    className="quiet-input"
                    placeholder={DEMO_CALL_COPY.mobilePlaceholder}
                    data-testid="demo-call-mobile"
                  />
                </div>
                <div className="quiet-field">
                  <label htmlFor="demo-website" className="quiet-field-label">
                    {DEMO_CALL_COPY.websiteLabel}
                  </label>
                  <input
                    id="demo-website"
                    name="website"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    value={fields.website}
                    onChange={handleField}
                    className="quiet-input"
                    placeholder={DEMO_CALL_COPY.websitePlaceholder}
                    data-testid="demo-call-website"
                  />
                </div>
              </div>

              {errorMessage && (
                <p className="quiet-demo-error" role="alert" data-testid="demo-call-error">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                className="quiet-cta quiet-cta-primary"
                data-testid="demo-call-submit"
                disabled={submitting}
              >
                {DEMO_CALL_COPY.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default DemoCallModal;
